import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { useTranslation } from 'react-i18next';

export default function AboutPage() {
  const { t } = useTranslation();
  const canonicalUrl = typeof window !== 'undefined' ? `${window.location.origin}/about` : '';
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫之家',
    url: canonicalUrl,
    description: '韩漫之家是专注于韩国漫画在线免费阅读的中文平台，提供无删减下拉式韩漫阅读服务。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hanmanzhijia.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫之家是什么网站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫之家是一个专注于韩国漫画在线免费阅读的中文平台，为读者提供海量优质韩漫资源，支持无删减下拉式阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫之家的漫画是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫之家所有漫画均为免费阅读。我们致力于为韩漫爱好者提供无障碍的阅读服务。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫之家多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫之家每日更新多部热门韩漫，确保读者能够第一时间看到最新的漫画内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在韩漫之家找到喜欢的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '你可以通过首页的分类导航浏览不同题材的韩漫，也可以使用搜索功能直接查找你喜欢的作品。',
        },
      },
    ],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9pdm88senbniolesteg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title="关于我们_韩漫之家_好看韩国漫画免费阅读平台"
        description="韩漫之家是专注于韩国漫画在线免费阅读的中文平台，提供无删减下拉式韩漫阅读服务。了解我们的品牌故事、平台口碑和联系方式。"
        keywords="韩漫之家, 关于韩漫之家, 韩漫阅读平台, 韩漫在线"
        canonicalUrl={canonicalUrl}
        schemaJson={{ ...orgSchema, ...faqSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), href: '/' },
                { label: t('breadcrumb.about') },
              ]}
            />

            {/* Hero */}
            <section className="py-12 md:py-16 text-center">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                {t('about.title')}
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                韩漫之家成立于2024年，是国内领先的韩国漫画在线阅读平台。我们致力于为广大韩漫爱好者提供海量优质、无删减的韩漫资源，打造舒适流畅的阅读体验。
              </p>
            </section>

            {/* Stats */}
            <section className="py-8 mb-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: '500+', label: t('about.stats.comics'), icon: 'ri-book-read-line' },
                  { value: '200万+', label: t('about.stats.users'), icon: 'ri-user-heart-line' },
                  { value: '3000+', label: t('about.stats.chapters'), icon: 'ri-file-list-3-line' },
                  { value: '2+', label: t('about.stats.years'), icon: 'ri-calendar-check-line' },
                ].map((stat, i) => (
                  <div
                    key={i}
                    className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center"
                  >
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                      <i className={`${stat.icon} text-lg`}></i>
                    </div>
                    <div className="font-heading text-2xl font-bold text-foreground-900">{stat.value}</div>
                    <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Brand Intro */}
            <section className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">{t('about.intro')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    韩漫之家以&quot;让每一位韩漫爱好者都能自由阅读&quot;为使命。我们深知韩漫文化的独特魅力，因此不断扩充漫画资源库，优化阅读体验，确保读者能够享受到最纯粹、最便捷的韩漫阅读服务。
                  </p>
                </div>
                <div className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">我们的优势</h3>
                  <ul className="text-sm text-foreground-600 space-y-2">
                    <li className="flex items-start gap-2">
                      <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-primary-500 text-sm"></i>
                      </div>
                      <span>海量韩漫资源，每日持续更新</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-primary-500 text-sm"></i>
                      </div>
                      <span>无删减下拉式阅读，流畅体验</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-primary-500 text-sm"></i>
                      </div>
                      <span>智能分类系统，精准推荐</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center mt-0.5">
                        <i className="ri-check-line text-primary-500 text-sm"></i>
                      </div>
                      <span>完全免费，无需注册付费</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">{t('about.reputation')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  {
                    name: '漫画爱好者小李',
                    comment: '韩漫之家是我用过最好用的韩漫阅读网站！分类清晰，更新及时，每次都能找到喜欢的漫画。',
                    rating: 5,
                  },
                  {
                    name: '追更达人小王',
                    comment: '每天都会打开韩漫之家看看有没有更新，下拉式阅读体验太棒了，画质也很清晰。',
                    rating: 5,
                  },
                  {
                    name: '韩漫新手小张',
                    comment: '朋友推荐的韩漫之家，刚开始看韩漫就被吸引了。推荐系统很智能，帮我找到了很多好作品。',
                    rating: 5,
                  },
                ].map((review, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex gap-0.5 mb-3">
                      {Array.from({ length: review.rating }).map((_, j) => (
                        <div key={j} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">&ldquo;{review.comment}&rdquo;</p>
                    <p className="text-xs text-foreground-500 font-medium">—— {review.name}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">常见问题</h2>
              <div className="space-y-3">
                {[
                  { q: '韩漫之家是什么网站？', a: '韩漫之家是一个专注于韩国漫画在线免费阅读的中文平台，为读者提供海量优质韩漫资源，支持无删减下拉式阅读体验。' },
                  { q: '韩漫之家的漫画是否免费？', a: '是的，韩漫之家所有漫画均为免费阅读。我们致力于为韩漫爱好者提供无障碍的阅读服务。' },
                  { q: '韩漫之家多久更新一次？', a: '韩漫之家每日更新多部热门韩漫，确保读者能够第一时间看到最新的漫画内容。' },
                  { q: '如何在韩漫之家找到喜欢的漫画？', a: '你可以通过首页的分类导航浏览不同题材的韩漫，也可以使用搜索功能直接查找你喜欢的作品。' },
                ].map((faq, i) => (
                  <details key={i} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-100 transition-colors duration-200">
                      <span className="text-sm font-medium text-foreground-800">{faq.q}</span>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-4">
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                      </div>
                    </summary>
                    <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section className="mb-16">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">{t('about.contact')}</h2>
              <div className="max-w-lg">
                <form onSubmit={handleSubmit} data-readdy-form="true">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                      <textarea
                        id="about-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>

                    {/* Honeypot */}
                    <input type="text" name="phone_alt" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-foreground-300 text-background-50 rounded-lg font-medium text-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                    </button>
                  </div>

                  {formStatus === 'success' && (
                    <div className="mt-4 p-3 rounded-lg bg-accent-100 text-accent-800 text-sm text-center">
                      感谢您的留言！我们会尽快回复。
                    </div>
                  )}
                  {formStatus === 'error' && formError && (
                    <div className="mt-4 p-3 rounded-lg bg-primary-100 text-primary-800 text-sm text-center">
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}